import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Box,
  Card,
  CardContent,
  Grid,
  Typography,
  CircularProgress,
  TextField,
  Divider,
  Chip,
  List,
  ListItem,
  ListItemText,
  Paper,
  FormControlLabel,
  Checkbox,
  IconButton,
  Tooltip,
  InputAdornment,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import InfoIcon from '@mui/icons-material/Info';
import {
  DataGridPro,
  GridToolbarContainer,
  GridToolbarColumnsButton,
  GridToolbarFilterButton,
  GridToolbarExportContainer,
  GridToolbarQuickFilter,
  GridCsvExportMenuItem,
} from '@mui/x-data-grid-pro';
import { MapContainer, TileLayer, Marker, Popup, Circle, useMap, useMapEvents } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import salesforceService from '../services/salesforceService';
import geocodingService from '../services/geocodingService';
import indexedDBService from '../services/indexedDBService';
import { useSnackbar } from 'notistack';
import { formatDate } from '../utils/dateUtils';
import { useSearchParams } from 'react-router-dom';


// Fix leaflet default icons
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

// Custom icon for selected lead (blue, larger, more prominent)
const selectedLeadIcon = new L.Icon({
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
  iconSize: [32, 52],
  iconAnchor: [16, 52],
  popupAnchor: [1, -34],
  shadowSize: [52, 52],
  className: 'selected-lead-marker',
});

// Custom icon for nearby location accounts (green)
const locationAccountIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-green.png',
  iconRetinaUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-green.png',
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
  className: 'location-account-marker',
});

// Helper function to get proxied image URL
const getProxiedImageUrl = (url) => {
  if (!url || !url.startsWith('https://www.purelocations.com.au/')) {
    return url;
  }
  const baseUrl = 'https://bdm.purelocations.com.au';
  return `${baseUrl}/api/image-proxy?url=${encodeURIComponent(url)}`;
};

// Thumbnail image with error fallback for DataGrid
const ScoutingThumbnail = ({ url }) => {
  const [error, setError] = useState(false);
  if (!url || typeof url !== 'string' || url.trim() === '') {
    return <Box sx={{ width: 48, height: 48, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'text.disabled', fontSize: '0.75rem' }}>—</Box>;
  }
  if (error) {
    return <Box sx={{ width: 48, height: 48, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'text.disabled', fontSize: '0.75rem', bgcolor: 'action.hover' }}>—</Box>;
  }
  const imgUrl = getProxiedImageUrl(url);
  return (
    <Box sx={{ width: 48, height: 48, overflow: 'hidden', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
      <img
        src={imgUrl}
        alt="Scouting"
        style={{ width: 48, height: 48, objectFit: 'cover', display: 'block' }}
        onError={() => setError(true)}
      />
    </Box>
  );
};

// Component to fit map bounds to markers
const FitBounds = ({ markers, center }) => {
  const map = useMap();
  useEffect(() => {
    if (!markers || markers.length === 0) return;
    const bounds = L.latLngBounds(markers.map(m => L.latLng(m[0], m[1])));
    if (center) {
      bounds.extend(L.latLng(center[0], center[1]));
    }
    map.fitBounds(bounds.pad(0.1));
  }, [markers, center, map]);
  return null;
};


const CustomToolbar = () => {
  return (
    <GridToolbarContainer sx={{ flexWrap: 'wrap', justifyContent: 'flex-start' }}>
      <GridToolbarColumnsButton />
      <GridToolbarFilterButton />
      <GridToolbarExportContainer>
        <GridCsvExportMenuItem options={{
          fileName: 'location_leads_export',
          utf8WithBom: true,
        }} />
      </GridToolbarExportContainer>
      <Box sx={{ flexGrow: 1 }} />
      <Box sx={{ minWidth: '320px' }}>
        <GridToolbarQuickFilter />
      </Box>
    </GridToolbarContainer>
  );
};

const DEFAULT_RADIUS_KM = 10;

const Registrations = () => {
  const [searchParams] = useSearchParams();
  const statusFilter = searchParams.get('status'); // Get status from URL query parameter
  
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedLead, setSelectedLead] = useState(null);
  const [mapCenter, setMapCenter] = useState([-33.8688, 151.2093]); // Default to Sydney
  const [mapZoom, setMapZoom] = useState(10);
  const [geocodingCache, setGeocodingCache] = useState({});
  const [radiusKm, setRadiusKm] = useState(DEFAULT_RADIUS_KM);
  const [priceRange, setPriceRange] = useState(''); // Price filter: '', '500-1500', '1501-2000', etc.
  const [locations, setLocations] = useState([]); // All location accounts from IndexedDB
  const [nearbyLocations, setNearbyLocations] = useState([]); // Filtered by radius
  const [allNearbyLocations, setAllNearbyLocations] = useState([]); // All locations within radius (not limited)
  const [showAllLocations, setShowAllLocations] = useState(false); // Toggle to show all vs top 10
  const [loadingLocations, setLoadingLocations] = useState(false);
  const [locationStats, setLocationStats] = useState({}); // Booking stats and fees
  const [manualAddress, setManualAddress] = useState(''); // Manual address lookup
  const [isManualLookup, setIsManualLookup] = useState(false); // Flag to track if current selection is manual lookup
  const [isGeocoding, setIsGeocoding] = useState(false); // Loading state for manual lookup
  const [selectedPlace, setSelectedPlace] = useState(null); // Store Google Autocomplete place selection
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [selectedLeadForDetails, setSelectedLeadForDetails] = useState(null);
  const autocompleteInputRef = useRef(null);
  const autocompleteRef = useRef(null);
  const { enqueueSnackbar } = useSnackbar();

  // Load location accounts from IndexedDB
  const loadLocations = useCallback(async () => {
    try {
      const locs = await indexedDBService.getAllLocations();
      setLocations(locs);
    } catch (error) {
      console.error('Error loading locations:', error);
    }
  }, []);

  const loadLeads = useCallback(async () => {
    try {
      setLoading(true);
      console.log('Loading location leads...');
      const data = await salesforceService.getLocationLeads();
      console.log(`Successfully loaded ${data?.length || 0} leads`);
      setLeads(data || []);
      
      if (!data || data.length === 0) {
        enqueueSnackbar('No location leads found', {
          variant: 'info',
          autoHideDuration: 4000,
        });
      }
    } catch (error) {
      console.error('Error loading leads:', error);
      const errorMessage = error.message || error.response?.data?.error || error.response?.data?.details || 'Unknown error';
      console.error('Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        stack: error.stack
      });
      
      enqueueSnackbar(`Failed to load leads: ${errorMessage}`, {
        variant: 'error',
        autoHideDuration: 8000,
      });
      
      // Set empty array on error so UI doesn't show loading forever
      setLeads([]);
    } finally {
      setLoading(false);
    }
  }, [enqueueSnackbar]);

  // Fetch booking stats and fees for locations
  const fetchLocationStats = useCallback(async (locationList, allLocationsList = null) => {
    try {
      const locationIds = locationList.map(loc => loc.accountId);
      
      // Fetch both booking stats and fees in parallel
      const [bookingStats, fees] = await Promise.all([
        salesforceService.getLocationBookingStats(locationIds),
        salesforceService.getLocationFees(locationIds),
      ]);

      // Update locationStats state - this will trigger the useEffect to re-sort and display
      setLocationStats(prevStats => {
        const updated = { ...prevStats };
        locationIds.forEach(id => {
          updated[id] = {
            bookingCount: bookingStats[id]?.bookingCount || 0,
            lastBookingDate: bookingStats[id]?.lastBookingDate || null,
            ownerFees: fees[id]?.ownerFees || null,
            imageUrl: fees[id]?.imageUrl || null,
          };
        });
        return updated;
      });
      
      setLoadingLocations(false);
      // The useEffect watching allNearbyLocations and locationStats will handle sorting and display
      
      // Trigger useEffect to update display if showAllLocations is true
      // This is done by updating locationStats which triggers the useEffect
    } catch (error) {
      console.error('Error fetching location stats:', error);
      // Set empty stats on error
      const emptyStats = {};
      locationList.forEach(loc => {
        emptyStats[loc.accountId] = {
          bookingCount: 0,
          lastBookingDate: null,
          ownerFees: null,
          imageUrl: null,
        };
      });
      setLocationStats(prevStats => ({ ...prevStats, ...emptyStats }));
      // Still set locations even on error, sorted by distance
      const locationsToSort = allLocationsList || locationList;
      const sortedLocations = [...locationsToSort].sort((a, b) => a.distanceKm - b.distanceKm);
      setNearbyLocations(sortedLocations.slice(0, 10));
      setLoadingLocations(false);
    }
  }, []);

  const geocodeAndUpdateMap = useCallback(async (lead) => {
    if (!lead) {
      console.warn('geocodeAndUpdateMap called with null/undefined lead');
      return;
    }

    // Build full address string
    const addressParts = [
      lead.Street,
      lead.City,
      lead.State,
      lead.PostalCode,
      lead.Country,
    ].filter(Boolean);

    if (addressParts.length === 0) {
      console.warn('No address parts found for lead:', lead);
      enqueueSnackbar('No address available for this lead', {
        variant: 'warning',
        autoHideDuration: 3000,
      });
      // Clear locations if no address
      setNearbyLocations([]);
      setAllNearbyLocations([]);
      return;
    }

    const fullAddress = addressParts.join(', ');
    console.log('Geocoding address for lead:', fullAddress);

    // Check cache first
    if (geocodingCache[fullAddress]) {
      const cached = geocodingCache[fullAddress];
      console.log('Using cached coordinates:', cached);
      setMapCenter(cached);
      setMapZoom(12);
      // The useEffect watching mapCenter will handle recalculation with current radius
      return;
    }

    try {
      console.log('Calling geocodingService.geocodeAddress with:', fullAddress);
      const coordinates = await geocodingService.geocodeAddress(fullAddress);
      console.log('Geocoding result:', coordinates);
      
      if (coordinates && Array.isArray(coordinates) && coordinates.length === 2) {
        setGeocodingCache(prev => ({
          ...prev,
          [fullAddress]: coordinates,
        }));
        setMapCenter(coordinates);
        setMapZoom(12);
        console.log('Successfully geocoded and updated map center');
        // The useEffect watching mapCenter will handle recalculation with current radius
      } else {
        console.warn('Geocoding returned invalid coordinates:', coordinates);
        enqueueSnackbar('Could not geocode address: ' + fullAddress, {
          variant: 'warning',
          autoHideDuration: 3000,
        });
        // Clear locations if geocoding fails
        setNearbyLocations([]);
        setAllNearbyLocations([]);
      }
    } catch (error) {
      console.error('Geocoding error:', error);
      console.error('Error details:', {
        message: error.message,
        stack: error.stack,
        lead: lead,
        address: fullAddress
      });
      enqueueSnackbar('Error geocoding address: ' + (error.message || 'Unknown error'), {
        variant: 'error',
        autoHideDuration: 4000,
      });
      // Clear locations on error
      setNearbyLocations([]);
      setAllNearbyLocations([]);
    }
  }, [geocodingCache, enqueueSnackbar]);

  // Load Google Maps Places API script
  useEffect(() => {
    const googleMapsApiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
    if (!googleMapsApiKey) {
      console.warn('REACT_APP_GOOGLE_MAPS_API_KEY not found. Google Autocomplete will not work.');
      return;
    }

    // Check if script is already loaded
    if (window.google && window.google.maps && window.google.maps.places) {
      console.log('Google Maps API already loaded');
      return;
    }

    // Check if script is already being loaded
    const existingScript = document.querySelector('script[src*="maps.googleapis.com"]');
    if (existingScript) {
      console.log('Google Maps API script already exists, waiting for load...');
      // Wait for it to load
      existingScript.addEventListener('load', () => {
        console.log('Google Maps API loaded from existing script');
      });
      return;
    }

    // Load the script with callback to avoid warning
    const callbackName = 'initGoogleMapsAutocomplete_' + Date.now();
    window[callbackName] = () => {
      console.log('Google Maps API loaded successfully');
      // Trigger a custom event so autocomplete initialization can react
      window.dispatchEvent(new Event('google-maps-loaded'));
      delete window[callbackName];
    };

    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${googleMapsApiKey}&libraries=places&callback=${callbackName}`;
    script.async = true;
    script.defer = true;
    script.onerror = () => {
      console.error('Failed to load Google Maps API');
      delete window[callbackName];
      enqueueSnackbar('Failed to load Google Maps API. Please check your API key.', {
        variant: 'error',
        autoHideDuration: 5000,
      });
    };
    document.head.appendChild(script);
  }, [enqueueSnackbar]);

  // Initialize Google Autocomplete when both API and ref are ready
  useEffect(() => {
    let checkInterval = null;
    let placeChangedListener = null;

    const initAutocomplete = () => {
      if (!window.google || !window.google.maps || !window.google.maps.places) {
        return false;
      }

      if (!autocompleteInputRef.current) {
        return false;
      }

      // Clean up existing autocomplete if it exists
      if (autocompleteRef.current && placeChangedListener) {
        window.google.maps.event.removeListener(placeChangedListener);
        placeChangedListener = null;
      }

      try {
        // Initialize autocomplete
        const autocomplete = new window.google.maps.places.Autocomplete(
          autocompleteInputRef.current,
          {
            types: ['address'],
            componentRestrictions: { country: 'au' }, // Restrict to Australia
          }
        );

        autocompleteRef.current = autocomplete;
        console.log('Google Autocomplete initialized successfully');

        // Handle place selection from autocomplete dropdown
        placeChangedListener = autocomplete.addListener('place_changed', () => {
          const place = autocomplete.getPlace();
          
          if (!place.geometry || !place.geometry.location) {
            return; // Don't show error, user might still be typing
          }

          console.log('Place selected from autocomplete:', place.formatted_address);

          // Store the selected place for use with lookup button
          setSelectedPlace(place);
          // Update input field with formatted address
          setManualAddress(place.formatted_address || '');
        });

        return true;
      } catch (error) {
        console.error('Error initializing Google Autocomplete:', error);
        return false;
      }
    };

    // Try to initialize immediately
    if (initAutocomplete()) {
      return () => {
        if (placeChangedListener && autocompleteRef.current) {
          window.google.maps.event.removeListener(placeChangedListener);
        }
      };
    }

    // If not ready, set up polling to check when both are available
    checkInterval = setInterval(() => {
      if (initAutocomplete()) {
        clearInterval(checkInterval);
        checkInterval = null;
      }
    }, 100);

    // Also listen for Google Maps API load event
    const handleGoogleMapsLoad = () => {
      if (initAutocomplete() && checkInterval) {
        clearInterval(checkInterval);
        checkInterval = null;
      }
    };
    window.addEventListener('google-maps-loaded', handleGoogleMapsLoad);

    // Cleanup after 10 seconds to avoid infinite polling
    const timeout = setTimeout(() => {
      if (checkInterval) {
        clearInterval(checkInterval);
        checkInterval = null;
      }
    }, 10000);

    return () => {
      if (checkInterval) {
        clearInterval(checkInterval);
      }
      clearTimeout(timeout);
      window.removeEventListener('google-maps-loaded', handleGoogleMapsLoad);
      if (placeChangedListener && autocompleteRef.current) {
        window.google.maps.event.removeListener(placeChangedListener);
      }
    };
  }, []); // Only run once on mount

  // Fetch leads and locations on mount
  useEffect(() => {
    loadLeads();
    loadLocations();
  }, [loadLeads, loadLocations]);

  // Geocode address and update map when lead is selected
  useEffect(() => {
    if (selectedLead && !isManualLookup) {
      // Clear nearby locations when selecting a new lead to force recalculation
      setNearbyLocations([]);
      setAllNearbyLocations([]);
      geocodeAndUpdateMap(selectedLead);
    } else if (selectedLead && isManualLookup) {
      // For manual lookup, coordinates are already set, just clear and let useEffect handle nearby locations
      setNearbyLocations([]);
      setAllNearbyLocations([]);
    }
  }, [selectedLead, isManualLookup, geocodeAndUpdateMap]);

  // Manual address lookup handler - uses Google Autocomplete formatted address
  const handleManualLookup = useCallback(async () => {
    if (!manualAddress || !manualAddress.trim()) {
      enqueueSnackbar('Please enter an address to search', {
        variant: 'warning',
        autoHideDuration: 3000,
      });
      return;
    }

    setIsGeocoding(true);

    try {
      // If we have a selected place from Google Autocomplete, use it
      if (selectedPlace && selectedPlace.geometry && selectedPlace.geometry.location) {
        const place = selectedPlace;
        const formattedAddress = place.formatted_address || manualAddress.trim();
        
        // Extract address components from Google place
        const addressComponents = {
          Street: '',
          City: '',
          State: '',
          PostalCode: '',
          Country: 'Australia',
        };

        place.address_components?.forEach((component) => {
          const types = component.types;
          if (types.includes('street_number')) {
            addressComponents.Street = component.long_name + ' ';
          } else if (types.includes('route')) {
            addressComponents.Street += component.long_name;
          } else if (types.includes('locality')) {
            addressComponents.City = component.long_name;
          } else if (types.includes('administrative_area_level_1')) {
            addressComponents.State = component.short_name;
          } else if (types.includes('postal_code')) {
            addressComponents.PostalCode = component.long_name;
          } else if (types.includes('country')) {
            addressComponents.Country = component.long_name;
          }
        });

        // Get coordinates from Google place geometry
        const lat = place.geometry.location.lat();
        const lng = place.geometry.location.lng();
        const coordinates = [lat, lng];

        // Create virtual lead from Google place data
        const virtualLead = {
          id: 'manual-lookup-' + Date.now(),
          Name: place.name || 'Manual Lookup',
          Company: null,
          Email: null,
          Phone: null,
          Street: addressComponents.Street.trim() || formattedAddress.split(',')[0] || '',
          City: addressComponents.City || '',
          State: addressComponents.State || '',
          PostalCode: addressComponents.PostalCode || '',
          Country: addressComponents.Country || 'Australia',
          Status: 'Manual Lookup',
          LeadSource: 'Manual Address Search',
          Description: `Manually searched address: ${formattedAddress}`,
          CreatedDate: new Date().toISOString(),
          LastModifiedDate: new Date().toISOString(),
          OwnerName: null,
        };

        // Update cache
        setGeocodingCache(prev => ({
          ...prev,
          [formattedAddress]: coordinates,
        }));

        // Set as selected lead and update map
        setIsManualLookup(true);
        setSelectedLead(virtualLead);
        setMapCenter(coordinates);
        setMapZoom(12);
        setIsGeocoding(false);
        return;
      }

      // Fallback: If no Google place selected, try to get place from autocomplete
      // This handles the case where user types and clicks lookup without selecting from dropdown
      if (autocompleteRef.current) {
        const place = autocompleteRef.current.getPlace();
        if (place && place.geometry && place.geometry.location) {
          // Process the place directly
          const formattedAddress = place.formatted_address || manualAddress.trim();
          
          // Extract address components from Google place
          const addressComponents = {
            Street: '',
            City: '',
            State: '',
            PostalCode: '',
            Country: 'Australia',
          };

          place.address_components?.forEach((component) => {
            const types = component.types;
            if (types.includes('street_number')) {
              addressComponents.Street = component.long_name + ' ';
            } else if (types.includes('route')) {
              addressComponents.Street += component.long_name;
            } else if (types.includes('locality')) {
              addressComponents.City = component.long_name;
            } else if (types.includes('administrative_area_level_1')) {
              addressComponents.State = component.short_name;
            } else if (types.includes('postal_code')) {
              addressComponents.PostalCode = component.long_name;
            } else if (types.includes('country')) {
              addressComponents.Country = component.long_name;
            }
          });

          // Get coordinates from Google place geometry
          const lat = place.geometry.location.lat();
          const lng = place.geometry.location.lng();
          const coordinates = [lat, lng];

          // Create virtual lead from Google place data
          const virtualLead = {
            id: 'manual-lookup-' + Date.now(),
            Name: place.name || 'Manual Lookup',
            Company: null,
            Email: null,
            Phone: null,
            Street: addressComponents.Street.trim() || formattedAddress.split(',')[0] || '',
            City: addressComponents.City || '',
            State: addressComponents.State || '',
            PostalCode: addressComponents.PostalCode || '',
            Country: addressComponents.Country || 'Australia',
            Status: 'Manual Lookup',
            LeadSource: 'Manual Address Search',
            Description: `Manually searched address: ${formattedAddress}`,
            CreatedDate: new Date().toISOString(),
            LastModifiedDate: new Date().toISOString(),
            OwnerName: null,
          };

          // Update cache
          setGeocodingCache(prev => ({
            ...prev,
            [formattedAddress]: coordinates,
          }));

          // Set as selected lead and update map
          setIsManualLookup(true);
          setSelectedLead(virtualLead);
          setMapCenter(coordinates);
          setMapZoom(12);
          setSelectedPlace(place);
          setIsGeocoding(false);
          return;
        }
      }

      // Final fallback: Use OpenStreetMap geocoding if Google Autocomplete didn't provide a place
      const addressToSearch = manualAddress.trim();
      
      // Check cache first
      if (geocodingCache[addressToSearch]) {
        const cached = geocodingCache[addressToSearch];
        createVirtualLeadFromCoordinates(cached, addressToSearch);
        setIsGeocoding(false);
        return;
      }

      // Geocode using OpenStreetMap as fallback
      const coordinates = await geocodingService.geocodeAddress(addressToSearch);
      
      if (!coordinates) {
        enqueueSnackbar('Could not geocode address. Please select an address from the suggestions or try a more specific address.', {
          variant: 'warning',
          autoHideDuration: 4000,
        });
        setIsGeocoding(false);
        return;
      }

      // Update cache
      setGeocodingCache(prev => ({
        ...prev,
        [addressToSearch]: coordinates,
      }));

      // Create virtual lead and update map
      createVirtualLeadFromCoordinates(coordinates, addressToSearch);
      setIsGeocoding(false);
    } catch (error) {
      console.error('Manual lookup error:', error);
      enqueueSnackbar('Error looking up address: ' + (error.message || 'Unknown error'), {
        variant: 'error',
        autoHideDuration: 4000,
      });
      setIsGeocoding(false);
    }
  }, [manualAddress, selectedPlace, geocodingCache, enqueueSnackbar]);

  // Create virtual lead from coordinates and address string
  const createVirtualLeadFromCoordinates = useCallback((coordinates, addressString) => {
    // Parse address string to extract components (basic parsing)
    const addressParts = addressString.split(',').map(part => part.trim());
    
    const virtualLead = {
      id: 'manual-lookup-' + Date.now(),
      Name: 'Manual Lookup',
      Company: null,
      Email: null,
      Phone: null,
      Street: addressParts[0] || addressString,
      City: addressParts.length > 1 ? addressParts[addressParts.length - 3] || '' : '',
      State: addressParts.length > 1 ? addressParts[addressParts.length - 2] || '' : '',
      PostalCode: addressParts.length > 1 ? addressParts[addressParts.length - 1]?.match(/\d+/)?.[0] || '' : '',
      Country: 'Australia',
      Status: 'Manual Lookup',
      LeadSource: 'Manual Address Search',
      Description: `Manually searched address: ${addressString}`,
      CreatedDate: new Date().toISOString(),
      LastModifiedDate: new Date().toISOString(),
      OwnerName: null,
    };

    // Set as selected lead and update map
    setIsManualLookup(true);
    setSelectedLead(virtualLead);
    setMapCenter(coordinates);
    setMapZoom(12);
  }, []);

  // Reset manual lookup flag when clicking on a real lead
  const handleRowClick = (params) => {
    setIsManualLookup(false);
    setManualAddress('');
    setSelectedPlace(null);
    setSelectedLead(params.row);
  };

  // Handle Enter key in address input
  const handleAddressKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleManualLookup();
    }
  };

  // Calculate nearby locations when center or radius changes
  useEffect(() => {
    if (!mapCenter || !locations || locations.length === 0) {
      setNearbyLocations([]);
      return;
    }

    setLoadingLocations(true);
    const [cLat, cLng] = mapCenter;
    const R = 6371; // Earth radius in km
    const toRad = (deg) => (deg * Math.PI) / 180;
    const results = [];
    
    // Use the actual radius value from state - MUST use the value set in the input field
    // Do NOT default to DEFAULT_RADIUS_KM - always use the actual value from radiusKm state
    const currentRadius = Number(radiusKm);
    
    // Validate radius value - if invalid, skip calculation (don't default)
    if (!isFinite(currentRadius) || currentRadius <= 0) {
      console.warn(`Invalid radius value: ${radiusKm}, skipping calculation`);
      setLoadingLocations(false);
      setNearbyLocations([]);
      setAllNearbyLocations([]);
      return;
    }
    
    // Debug: Log the radius being used
    console.log(`Calculating nearby locations with radius: ${currentRadius} km`);

    for (const loc of locations) {
      // Skip inactive locations
      if (loc.status === 'Inactive Location') {
        continue;
      }

      const lat = Number(loc.latitude);
      const lng = Number(loc.longitude);
      if (!isFinite(lat) || !isFinite(lng)) {
        continue;
      }

      const dLat = toRad(lat - cLat);
      const dLng = toRad(lng - cLng);
      const a = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(cLat)) * Math.cos(toRad(lat)) * Math.sin(dLng / 2) ** 2;
      const distance = 2 * R * Math.asin(Math.sqrt(a));

      if (distance <= currentRadius) {
        results.push({
          accountId: loc.accountId,
          name: loc.name,
          address: loc.address,
          design: loc.design,
          status: loc.status,
          locationTier: loc.locationTier,
          lat,
          lng,
          distanceKm: Number(distance.toFixed(2)),
        });
      }
    }

    // Store all results
    setAllNearbyLocations(results);
    
    if (results.length === 0) {
      setLocationStats({});
      setNearbyLocations([]);
      setLoadingLocations(false);
      return;
    }
    
    // Clear stats for locations that are no longer in the radius
    setLocationStats(prevStats => {
      const currentLocationIds = new Set(results.map(loc => loc.accountId));
      const filteredStats = {};
      Object.keys(prevStats).forEach(id => {
        if (currentLocationIds.has(id)) {
          filteredStats[id] = prevStats[id];
        }
      });
      return filteredStats;
    });
    
    // Fetch booking stats and fees for ALL locations within the radius
    // This is necessary to find the top 10 by booking count (not just top 10 by distance)
    // The user wants the top 10 results by booking within the selected radius
    fetchLocationStats(results, results);
  }, [mapCenter, radiusKm, locations, fetchLocationStats]);

  // Helper function to check if a price matches the selected range
  const matchesPriceRange = useCallback((ownerFees, range) => {
    if (!range || range === '') {
      return true; // No filter selected, show all
    }
    
    if (ownerFees === null || ownerFees === undefined) {
      return false; // No price data, exclude
    }
    
    const price = typeof ownerFees === 'number' ? ownerFees : Number(ownerFees);
    if (isNaN(price)) {
      return false; // Invalid price, exclude
    }
    
    switch (range) {
      case '500-1500':
        return price >= 500 && price <= 1500;
      case '1501-2000':
        return price >= 1501 && price <= 2000;
      case '2001-2500':
        return price >= 2001 && price <= 2500;
      case '2501-3000':
        return price >= 2501 && price <= 3000;
      case '3001-3500':
        return price >= 3001 && price <= 3500;
      case '3501-4000':
        return price >= 3501 && price <= 4000;
      case '4000+':
        return price > 4000;
      default:
        return true;
    }
  }, []);

  // Update displayed locations based on showAllLocations toggle, price range, and when allNearbyLocations changes
  useEffect(() => {
    if (allNearbyLocations.length === 0) {
      setNearbyLocations([]);
      return;
    }
    
    // Filter locationStats to only include locations that are still in allNearbyLocations
    // This ensures we don't show stats for locations outside the current radius
    const currentLocationIds = new Set(allNearbyLocations.map(loc => loc.accountId));
    const filteredStats = {};
    Object.keys(locationStats).forEach(id => {
      if (currentLocationIds.has(id)) {
        filteredStats[id] = locationStats[id];
      }
    });
    
    // Check if we need to fetch stats first
    const allLocationIds = allNearbyLocations.map(loc => loc.accountId);
    const existingIds = Object.keys(filteredStats);
    const missingIds = allLocationIds.filter(id => !existingIds.includes(id));
    
    // If we have missing stats, fetch them first (the useEffect will run again after stats are loaded)
    if (missingIds.length > 0) {
      const missingLocations = allNearbyLocations.filter(loc => missingIds.includes(loc.accountId));
      fetchLocationStats(missingLocations, allNearbyLocations);
      return; // Exit early, will re-run after stats are fetched
    }
    
    // Now that we have all stats, filter by price range if a filter is selected
    let locationsToProcess = allNearbyLocations;
    if (priceRange) {
      locationsToProcess = allNearbyLocations.filter(loc => {
        const stats = filteredStats[loc.accountId];
        return matchesPriceRange(stats?.ownerFees, priceRange);
      });
    }
    
    if (showAllLocations) {
      // Already have all stats, just sort and display
      const sortedLocations = [...locationsToProcess].sort((a, b) => {
        const aBookings = filteredStats[a.accountId]?.bookingCount || 0;
        const bBookings = filteredStats[b.accountId]?.bookingCount || 0;
        if (bBookings !== aBookings) {
          return bBookings - aBookings;
        }
        return a.distanceKm - b.distanceKm;
      });
      setNearbyLocations(sortedLocations);
    } else {
      // Show top 10 by booking count (not top 10 by distance)
      // First sort all by booking count, then take top 10
      const sortedByBookings = [...locationsToProcess].sort((a, b) => {
        const aBookings = filteredStats[a.accountId]?.bookingCount || 0;
        const bBookings = filteredStats[b.accountId]?.bookingCount || 0;
        if (bBookings !== aBookings) {
          return bBookings - aBookings;
        }
        return a.distanceKm - b.distanceKm;
      });
      setNearbyLocations(sortedByBookings.slice(0, 10));
    }
  }, [showAllLocations, allNearbyLocations, locationStats, priceRange, matchesPriceRange, fetchLocationStats]);


  // Prepare map markers for nearby locations (include all locations when showing all)
  const mapMarkers = useMemo(() => {
    const markers = [];
    if (mapCenter) {
      markers.push(mapCenter); // Selected lead location
    }
    // nearbyLocations is already filtered by price range in the useEffect
    // So we can use it directly regardless of showAllLocations
    nearbyLocations.forEach(loc => {
      markers.push([loc.lat, loc.lng]);
    });
    return markers;
  }, [mapCenter, nearbyLocations]);

  const processRowUpdate = async (newRow) => {
    try {
      // Extract only the fields that can be updated
      const updateFields = {};
      const updatableFields = ['Name', 'Company', 'Email', 'Phone', 'Street', 'City', 'State', 'PostalCode', 'Status', 'Description'];
      
      updatableFields.forEach(field => {
        if (newRow[field] !== undefined && newRow[field] !== null) {
          updateFields[field] = newRow[field];
        }
      });

      await salesforceService.updateLead(newRow.id, updateFields);
      
      // Update local state
      setLeads(prevLeads =>
        prevLeads.map(lead =>
          lead.id === newRow.id ? { ...lead, ...updateFields } : lead
        )
      );

      // Update selected lead if it's the one being edited
      if (selectedLead && selectedLead.id === newRow.id) {
        setSelectedLead({ ...selectedLead, ...updateFields });
      }

      enqueueSnackbar('Lead updated successfully', {
        variant: 'success',
        autoHideDuration: 3000,
      });

      return newRow;
    } catch (error) {
      console.error('Error updating lead:', error);
      enqueueSnackbar('Failed to update lead: ' + (error.message || 'Unknown error'), {
        variant: 'error',
        autoHideDuration: 6000,
      });
      throw error; // Re-throw to prevent DataGrid from updating
    }
  };

  const handleProcessRowUpdateError = (error) => {
    console.error('Row update error:', error);
  };

  // Function to get color for each status
  const getStatusColor = (status) => {
    if (!status) return 'default';
    
    const statusLower = status.toLowerCase();
    
    // Map each status to a unique color
    const colorMap = {
      'new': 'info',
      'pending approval': 'warning',
      'waiting on fees/images': 'error',
      'listing queue': 'success',
      'open - not contacted': 'default',
    };
    
    // Check for exact match first
    if (colorMap[statusLower]) {
      return colorMap[statusLower];
    }
    
    // Check for partial matches (for truncated statuses)
    for (const [key, color] of Object.entries(colorMap)) {
      if (statusLower.includes(key) || key.includes(statusLower)) {
        return color;
      }
    }
    
    // Default color for unknown statuses
    return 'primary';
  };

  const handleOpenSalesforce = (leadId) => {
    const sfBaseUrl = process.env.REACT_APP_SALESFORCE_URL || 'https://purelocations.lightning.force.com';
    const sfUrl = `${sfBaseUrl}/lightning/r/Lead/${leadId}/view`;
    window.open(sfUrl, '_blank');
  };

  const handleOpenDetailsDialog = (lead) => {
    setSelectedLeadForDetails(lead);
    setDetailsDialogOpen(true);
  };

  const handleCloseDetailsDialog = () => {
    setDetailsDialogOpen(false);
    setSelectedLeadForDetails(null);
  };

  const columns = [
    {
      field: 'Scouting_Image_URL__c',
      headerName: 'Image',
      width: 70,
      minWidth: 70,
      flex: 0,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params) => <ScoutingThumbnail url={params.value} />,
    },
    {
      field: 'Status',
      headerName: 'Status',
      flex: 0.8,
      minWidth: 120,
      editable: true,
      renderCell: (params) => (
        <Chip
          label={params.value || 'N/A'}
          size="small"
          color={getStatusColor(params.value)}
        />
      ),
    },
    {
      field: 'Name',
      headerName: 'Name',
      flex: 1,
      minWidth: 150,
      editable: true,
    },
    {
      field: 'Street',
      headerName: 'Address',
      flex: 1.2,
      minWidth: 200,
      editable: true,
    },
    {
      field: 'City',
      headerName: 'City',
      flex: 0.8,
      minWidth: 120,
      editable: true,
    },
    {
      field: 'State',
      headerName: 'State',
      flex: 0.6,
      minWidth: 100,
      editable: true,
    },
    {
      field: 'PostalCode',
      headerName: 'Postcode',
      flex: 0.6,
      minWidth: 100,
      editable: true,
    },
    {
      field: 'tools',
      headerName: 'Tools',
      flex: 0.8,
      minWidth: 120,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params) => {
        const lead = params.row;
        return (
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            <Tooltip title="Open in Salesforce">
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  handleOpenSalesforce(lead.id);
                }}
                color="primary"
              >
                <OpenInNewIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="View Details">
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  handleOpenDetailsDialog(lead);
                }}
                color="info"
              >
                <InfoIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        );
      },
    },
    {
      field: 'LastModifiedDate',
      headerName: 'Modified',
      flex: 1,
      minWidth: 150,
      type: 'dateTime',
      valueGetter: (value, row) => {
        return row.LastModifiedDate ? new Date(row.LastModifiedDate) : null;
      },
    },
    {
      field: 'CreatedDate',
      headerName: 'Created',
      flex: 1,
      minWidth: 150,
      type: 'dateTime',
      valueGetter: (value, row) => {
        return row.CreatedDate ? new Date(row.CreatedDate) : null;
      },
    },
  ];

  return (
    <Box sx={{ height: 'calc(100vh - 88px)', overflow: 'hidden', p: 2 }}>
      <Grid container spacing={2} sx={{ height: '100%', overflow: 'hidden' }}>
        {/* Left Panel - DataGrid */}
        <Grid item xs={12} md={6} sx={{ height: '100%', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
          <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', p: 0, minHeight: 0, overflow: 'hidden', '&:last-child': { pb: 0 } }}>
              <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider', flexShrink: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                  <Typography variant="h6">Location Leads</Typography>
                  {statusFilter && (
                    <Chip 
                      label={`Filtered: ${statusFilter}`}
                      color="primary"
                      size="small"
                      onDelete={() => {
                        // Remove status filter from URL
                        const newSearchParams = new URLSearchParams(searchParams);
                        newSearchParams.delete('status');
                        window.history.replaceState({}, '', `/registrations${newSearchParams.toString() ? '?' + newSearchParams.toString() : ''}`);
                        window.location.reload(); // Reload to clear filter
                      }}
                    />
                  )}
                </Box>
                <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="Enter address (e.g., 24 Gilbert St, Brisbane)"
                    value={manualAddress}
                    inputRef={autocompleteInputRef}
                    onChange={(e) => {
                      setManualAddress(e.target.value);
                      // Clear selected place when user types
                      if (selectedPlace) {
                        setSelectedPlace(null);
                      }
                    }}
                    onKeyPress={handleAddressKeyPress}
                    disabled={isGeocoding}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon />
                        </InputAdornment>
                      ),
                    }}
                    helperText="Type an address, select from suggestions, then click Lookup"
                  />
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleManualLookup}
                    disabled={isGeocoding || !manualAddress.trim()}
                    sx={{ minWidth: 100, alignSelf: 'flex-start', mt: 0.5 }}
                  >
                    {isGeocoding ? 'Looking up...' : 'Lookup'}
                  </Button>
                </Box>
              </Box>
              <Box sx={{ flex: 1, overflow: 'hidden', minHeight: 0 }}>
                {loading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                    <CircularProgress />
                  </Box>
                ) : (
                  <DataGridPro
                    rows={leads}
                    columns={columns}
                    getRowId={(row) => row.id}
                    density="comfortable"
                    pagination
                    autoHeight={false}
                    disableRowSelectionOnClick
                    onRowClick={handleRowClick}
                    processRowUpdate={processRowUpdate}
                    onProcessRowUpdateError={handleProcessRowUpdateError}
                    rowSelectionModel={selectedLead ? [selectedLead.id] : []}
                    slots={{
                      toolbar: CustomToolbar,
                    }}
                    getRowClassName={(params) => {
                      return params.id === selectedLead?.id ? 'selected-row' : '';
                    }}
                    sx={{
                      height: '100%',
                      width: '100%',
                      '& .MuiDataGrid-main': { 
                        overflow: 'hidden',
                      },
                      '& .MuiDataGrid-row:hover': {
                        backgroundColor: 'rgba(25, 118, 210, 0.04)',
                        cursor: 'pointer',
                      },
                      '& .MuiDataGrid-row.selected-row': {
                        backgroundColor: 'rgba(25, 118, 210, 0.08)',
                        '&:hover': {
                          backgroundColor: 'rgba(25, 118, 210, 0.12)',
                        },
                      },
                    }}
                    initialState={{
                      pagination: { pageSize: 25 },
                      sorting: {
                        sortModel: [{ field: 'CreatedDate', sort: 'desc' }],
                      },
                      filter: {
                        filterModel: {
                          items: statusFilter ? [
                            {
                              field: 'Status',
                              operator: 'equals',
                              value: statusFilter,
                            }
                          ] : [],
                        },
                      },
                    }}
                  />
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Right Panel - Map and Details */}
        <Grid item xs={12} md={6} sx={{ height: '100%' }}>
          <Grid container spacing={2} sx={{ height: '100%' }}>
            {/* Map Section - Full Height */}
            <Grid item xs={12} sx={{ height: '100%' }}>
              <Card sx={{ height: '100%' }}>
                <CardContent sx={{ height: '100%', p: 0, '&:last-child': { pb: 0 } }}>
                  <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                    <Typography variant="h6">Location Map</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <TextField
                        type="number"
                        label="Radius (km)"
                        value={radiusKm}
                        onChange={(e) => {
                          const value = e.target.value;
                          // Set the value directly - use actual input value, not a default
                          if (value === '') {
                            setRadiusKm(DEFAULT_RADIUS_KM); // Only default when empty
                          } else {
                            const numValue = Number(value);
                            // Only update if it's a valid positive number
                            if (!isNaN(numValue) && numValue > 0) {
                              setRadiusKm(numValue);
                            }
                          }
                        }}
                        size="small"
                        sx={{ width: 120 }}
                        InputProps={{
                          inputProps: { min: 1, max: 100, step: 1 }
                        }}
                      />
                      <FormControl size="small" sx={{ minWidth: 180 }}>
                        <InputLabel>8hr Owner Fee</InputLabel>
                        <Select
                          value={priceRange}
                          label="8hr Owner Fee"
                          onChange={(e) => setPriceRange(e.target.value)}
                        >
                          <MenuItem value="">All Prices</MenuItem>
                          <MenuItem value="500-1500">$500 - $1,500</MenuItem>
                          <MenuItem value="1501-2000">$1,501 - $2,000</MenuItem>
                          <MenuItem value="2001-2500">$2,001 - $2,500</MenuItem>
                          <MenuItem value="2501-3000">$2,501 - $3,000</MenuItem>
                          <MenuItem value="3001-3500">$3,001 - $3,500</MenuItem>
                          <MenuItem value="3501-4000">$3,501 - $4,000</MenuItem>
                          <MenuItem value="4000+">$4,000+</MenuItem>
                        </Select>
                      </FormControl>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={showAllLocations}
                            onChange={(e) => setShowAllLocations(e.target.checked)}
                            size="small"
                          />
                        }
                        label="Show All"
                      />
                    </Box>
                  </Box>
                  <Box sx={{ height: 'calc(100% - 64px)', display: 'flex' }}>
                    {/* Left Half - Map */}
                    <Box sx={{ width: '50%', height: '100%', position: 'relative' }}>
                      <MapContainer
                        center={mapCenter}
                        zoom={mapZoom}
                        style={{ height: '100%', width: '100%' }}
                        key={`${mapCenter[0]}-${mapCenter[1]}-${radiusKm}-${nearbyLocations.length}-${showAllLocations}-${priceRange}`}
                      >
                        <TileLayer
                          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                        />
                        {(selectedLead && mapCenter) && (
                          <>
                            <Marker position={mapCenter} icon={selectedLeadIcon}>
                              <Popup maxWidth={300}>
                                <div style={{ minWidth: '250px' }}>
                                  <div style={{ marginBottom: '8px', fontWeight: 'bold', fontSize: '14px' }}>
                                    {selectedLead.Name}
                                  </div>
                                  {selectedLead.Company && (
                                    <div style={{ marginBottom: '4px', fontSize: '12px', color: '#666' }}>
                                      {selectedLead.Company}
                                    </div>
                                  )}
                                  {(selectedLead.Street || selectedLead.City || selectedLead.State || selectedLead.PostalCode) && (
                                    <div style={{ marginBottom: '8px', fontSize: '11px', color: '#666' }}>
                                      {[
                                        selectedLead.Street,
                                        selectedLead.City,
                                        selectedLead.State,
                                        selectedLead.PostalCode,
                                      ]
                                        .filter(Boolean)
                                        .join(', ')}
                                    </div>
                                  )}
                                  <div style={{ fontSize: '11px', color: '#1976d2', fontWeight: 'bold' }}>
                                    {isManualLookup ? 'Manual Lookup' : 'Selected Lead'}
                                  </div>
                                </div>
                              </Popup>
                            </Marker>
                            <Circle
                              center={mapCenter}
                              radius={radiusKm * 1000}
                              pathOptions={{ color: '#1976d2', fillOpacity: 0.1 }}
                            />
                          </>
                        )}
                        {nearbyLocations.map((loc, index) => {
                          const stats = locationStats[loc.accountId] || {};
                          const hasNoBookings = (stats.bookingCount || 0) === 0;
                          // Use red icon for locations with no bookings, green for others
                          const markerIcon = hasNoBookings ? 
                            new L.Icon({
                              iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-red.png',
                              iconRetinaUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-red.png',
                              shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
                              iconSize: [25, 41],
                              iconAnchor: [12, 41],
                              popupAnchor: [1, -34],
                              shadowSize: [41, 41],
                            }) : locationAccountIcon;
                          
                          const imageUrl = stats.imageUrl ? getProxiedImageUrl(stats.imageUrl) : null;
                          const ownerFees = stats.ownerFees !== null && stats.ownerFees !== undefined 
                            ? (typeof stats.ownerFees === 'number' ? stats.ownerFees.toLocaleString() : stats.ownerFees)
                            : null;
                          const bookingCount = stats.bookingCount || 0;
                          const lastBookingDate = stats.lastBookingDate 
                            ? formatDate(stats.lastBookingDate) 
                            : null;
                          
                          return (
                            <Marker
                              key={loc.accountId}
                              position={[loc.lat, loc.lng]}
                              icon={markerIcon}
                            >
                              <Popup maxWidth={300}>
                                <div style={{ minWidth: '250px' }}>
                                  {/* Thumbnail Image */}
                                  {imageUrl && (
                                    <div style={{ marginBottom: '8px' }}>
                                      <img 
                                        src={imageUrl} 
                                        alt={loc.name}
                                        style={{
                                          width: '100%',
                                          height: '120px',
                                          objectFit: 'cover',
                                          borderRadius: '4px',
                                          display: 'block'
                                        }}
                                        onError={(e) => {
                                          e.target.style.display = 'none';
                                        }}
                                      />
                                    </div>
                                  )}
                                  {/* Name */}
                                  <div style={{ marginBottom: '6px', fontWeight: 'bold', fontSize: '14px' }}>
                                    {loc.name}
                                  </div>
                                  {/* Address */}
                                  {loc.address && (
                                    <div style={{ marginBottom: '6px', fontSize: '11px', color: '#666' }}>
                                      {loc.address}
                                    </div>
                                  )}
                                  {/* Distance */}
                                  <div style={{ marginBottom: '8px', fontSize: '11px', color: '#1976d2', fontWeight: 'bold' }}>
                                    {loc.distanceKm} km away
                                  </div>
                                  {/* Price */}
                                  {ownerFees && (
                                    <div style={{ marginBottom: '4px', fontSize: '12px' }}>
                                      <strong>8hr Owner Fee:</strong> ${ownerFees}
                                    </div>
                                  )}
                                  {/* Bookings */}
                                  <div style={{ marginBottom: '4px', fontSize: '12px', color: hasNoBookings ? '#f44336' : 'inherit', fontWeight: hasNoBookings ? 'bold' : 'normal' }}>
                                    <strong>Total Bookings:</strong> {bookingCount}
                                  </div>
                                  {/* Last Booking Date */}
                                  {lastBookingDate ? (
                                    <div style={{ marginBottom: '4px', fontSize: '11px', color: '#666' }}>
                                      <strong>Last Booked:</strong> {lastBookingDate}
                                    </div>
                                  ) : hasNoBookings ? (
                                    <div style={{ fontSize: '11px', color: '#f44336', fontWeight: 'bold' }}>
                                      No bookings in last 12 months
                                    </div>
                                  ) : null}
                                </div>
                              </Popup>
                            </Marker>
                          );
                        })}
                        <FitBounds markers={mapMarkers} center={mapCenter} />
                      </MapContainer>
                    </Box>
                    {/* Right Half - Nearby Locations List */}
                    <Box sx={{ width: '50%', height: '100%', borderLeft: 1, borderColor: 'divider', overflow: 'auto' }}>
                      <Box sx={{ p: 2 }}>
                        <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'bold' }}>
                          {showAllLocations 
                            ? `Nearby Locations (${nearbyLocations.length}${priceRange ? `, filtered by price` : ''})`
                            : `Nearby Locations (showing top 10 by bookings${priceRange ? `, filtered by price` : ''})`
                          }
                        </Typography>
                        {loadingLocations ? (
                          <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                            <CircularProgress size={24} />
                          </Box>
                        ) : (
                          <List dense>
                            {/* Selected Lead at top */}
                            {selectedLead && mapCenter && (
                              <Paper elevation={1} sx={{ mb: 1, p: 1, backgroundColor: 'rgba(25, 118, 210, 0.08)' }}>
                                <ListItem>
                                  <ListItemText
                                    primary={
                                      <Typography component="span" variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                                        {selectedLead.Name}
                                      </Typography>
                                    }
                                    secondary={
                                      <Box component="span">
                                        {selectedLead.Company && (
                                          <Typography component="span" variant="caption" display="block">
                                            {selectedLead.Company}
                                          </Typography>
                                        )}
                                        <Typography component="span" variant="caption" display="block" color="text.secondary">
                                          {[
                                            selectedLead.Street,
                                            selectedLead.City,
                                            selectedLead.State,
                                            selectedLead.PostalCode,
                                          ]
                                            .filter(Boolean)
                                            .join(', ')}
                                        </Typography>
                                        <Chip
                                          label={isManualLookup ? 'Manual Lookup' : 'Selected Lead'}
                                          size="small"
                                          color={isManualLookup ? 'secondary' : 'primary'}
                                          sx={{ mt: 0.5 }}
                                        />
                                      </Box>
                                    }
                                  />
                                </ListItem>
                              </Paper>
                            )}
                            {/* Nearby Locations */}
                            {nearbyLocations.length === 0 ? (
                              <ListItem>
                                <ListItemText
                                  primary={
                                    <Typography variant="body2" color="text.secondary">
                                      {selectedLead
                                        ? 'No locations found within radius'
                                        : 'Select a lead to see nearby locations'}
                                    </Typography>
                                  }
                                />
                              </ListItem>
                            ) : (
                              nearbyLocations.map((loc) => {
                                const stats = locationStats[loc.accountId] || {};
                                const hasNoBookings = (stats.bookingCount || 0) === 0;
                                return (
                                  <ListItem 
                                    key={loc.accountId} 
                                    sx={{
                                      borderBottom: 1, 
                                      borderColor: 'divider',
                                      backgroundColor: hasNoBookings ? 'rgba(244, 67, 54, 0.1)' : 'transparent',
                                      '&:hover': {
                                        backgroundColor: hasNoBookings ? 'rgba(244, 67, 54, 0.15)' : 'rgba(0, 0, 0, 0.04)',
                                      }
                                    }}
                                  >
                                    <ListItemText
                                      primary={
                                        <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                          <Typography component="span" variant="subtitle2">
                                            {loc.name}
                                          </Typography>
                                          {hasNoBookings && (
                                            <Chip
                                              label="No Bookings"
                                              size="small"
                                              color="error"
                                              sx={{ height: 20, fontSize: '0.65rem' }}
                                            />
                                          )}
                                        </Box>
                                      }
                                      secondary={
                                        <Box component="span">
                                          <Typography component="span" variant="caption" display="block">
                                            {loc.address}
                                          </Typography>
                                          {loc.design && (
                                            <Typography component="span" variant="caption" display="block" color="text.secondary">
                                              Design: {loc.design}
                                            </Typography>
                                          )}
                                          <Typography component="span" variant="caption" display="block" color="primary" sx={{ fontWeight: 'bold' }}>
                                            {loc.distanceKm} km away
                                          </Typography>
                                          {stats.ownerFees !== null && stats.ownerFees !== undefined && (
                                            <Typography component="span" variant="caption" display="block" sx={{ mt: 0.5 }}>
                                              <strong>8hr Owner Fee:</strong> ${typeof stats.ownerFees === 'number' ? stats.ownerFees.toLocaleString() : stats.ownerFees}
                                            </Typography>
                                          )}
                                          <Typography 
                                            component="span"
                                            variant="caption" 
                                            display="block" 
                                            sx={{ 
                                              mt: 0.5,
                                              color: hasNoBookings ? 'error.main' : 'inherit',
                                              fontWeight: hasNoBookings ? 'bold' : 'normal'
                                            }}
                                          >
                                            <strong>Bookings (12mo):</strong> {stats.bookingCount || 0}
                                          </Typography>
                                          {stats.lastBookingDate && (
                                            <Typography component="span" variant="caption" display="block" color="text.secondary">
                                              Last booking: {formatDate(stats.lastBookingDate)}
                                            </Typography>
                                          )}
                                        </Box>
                                      }
                                    />
                                  </ListItem>
                                );
                              })
                            )}
                          </List>
                        )}
                      </Box>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Grid>
      </Grid>

      {/* Location Lead Details Dialog */}
      <Dialog
        open={detailsDialogOpen}
        onClose={handleCloseDetailsDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">Location Lead Details</Typography>
            {selectedLeadForDetails && (
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Tooltip title="Open in Salesforce">
                  <IconButton
                    size="small"
                    onClick={() => {
                      const sfBaseUrl = process.env.REACT_APP_SALESFORCE_URL || 'https://purelocations.lightning.force.com';
                      const leadId = selectedLeadForDetails.id;
                      const sfUrl = `${sfBaseUrl}/lightning/r/Lead/${leadId}/view`;
                      window.open(sfUrl, '_blank');
                    }}
                  >
                    <OpenInNewIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            )}
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedLeadForDetails ? (
            <Box>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Name
                  </Typography>
                  <Typography variant="body1">{selectedLeadForDetails.Name || 'N/A'}</Typography>
                </Grid>

                <Grid item xs={12}>
                  <Divider />
                </Grid>

                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Company
                  </Typography>
                  <Typography variant="body1">{selectedLeadForDetails.Company || 'N/A'}</Typography>
                </Grid>

                <Grid item xs={12}>
                  <Divider />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Email
                  </Typography>
                  <Typography variant="body1">
                    {selectedLeadForDetails.Email ? (
                      <a href={`mailto:${selectedLeadForDetails.Email}`}>{selectedLeadForDetails.Email}</a>
                    ) : (
                      'N/A'
                    )}
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Phone
                  </Typography>
                  <Typography variant="body1">
                    {selectedLeadForDetails.Phone ? (
                      <a href={`tel:${selectedLeadForDetails.Phone}`}>{selectedLeadForDetails.Phone}</a>
                    ) : (
                      'N/A'
                    )}
                  </Typography>
                </Grid>

                <Grid item xs={12}>
                  <Divider />
                </Grid>

                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Address
                  </Typography>
                  <Typography variant="body1">
                    {[
                      selectedLeadForDetails.Street,
                      selectedLeadForDetails.City,
                      selectedLeadForDetails.State,
                      selectedLeadForDetails.PostalCode,
                      selectedLeadForDetails.Country,
                    ]
                      .filter(Boolean)
                      .join(', ') || 'N/A'}
                  </Typography>
                </Grid>

                <Grid item xs={12}>
                  <Divider />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Status
                  </Typography>
                  <Chip
                    label={selectedLeadForDetails.Status || 'N/A'}
                    size="small"
                    color={getStatusColor(selectedLeadForDetails.Status)}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Lead Source
                  </Typography>
                  <Typography variant="body1">{selectedLeadForDetails.LeadSource || 'N/A'}</Typography>
                </Grid>

                {selectedLeadForDetails.Description && (
                  <>
                    <Grid item xs={12}>
                      <Divider />
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Notes
                      </Typography>
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                        {selectedLeadForDetails.Description}
                      </Typography>
                    </Grid>
                  </>
                )}

                <Grid item xs={12}>
                  <Divider />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Created Date
                  </Typography>
                  <Typography variant="body2">
                    {selectedLeadForDetails.CreatedDate
                      ? new Date(selectedLeadForDetails.CreatedDate).toLocaleString()
                      : 'N/A'}
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Last Modified
                  </Typography>
                  <Typography variant="body2">
                    {selectedLeadForDetails.LastModifiedDate
                      ? new Date(selectedLeadForDetails.LastModifiedDate).toLocaleString()
                      : 'N/A'}
                  </Typography>
                </Grid>

                {selectedLeadForDetails.OwnerName && (
                  <>
                    <Grid item xs={12}>
                      <Divider />
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Owner
                      </Typography>
                      <Typography variant="body2">{selectedLeadForDetails.OwnerName}</Typography>
                    </Grid>
                  </>
                )}
              </Grid>
            </Box>
          ) : (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="body2" color="text.secondary">
                No lead selected
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDetailsDialog}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Registrations;
